/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';

import { css, jsx, keyframes } from '@compiled/react';
import { useIntl } from 'react-intl-next';

import Button, { IconButton } from '@atlaskit/button/new';
import { aiSuggestionsMessages } from '@atlaskit/editor-common/ai-messages';
import Heading from '@atlaskit/heading';
import { IconTile } from '@atlaskit/icon';
import CheckMarkIcon from '@atlaskit/icon/core/check-mark';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import CrossIcon from '@atlaskit/icon/core/cross';
import DeleteIcon from '@atlaskit/icon/core/delete';
import LockLockedIcon from '@atlaskit/icon/core/lock-locked';
import { RovoIcon } from '@atlaskit/logo';
import { Flex, Inline, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

const stackTiltIn = keyframes({
	from: { transform: 'rotate(0deg)' },
	to: { transform: 'rotate(3deg)' },
});

const cardStyles = css({
	position: 'relative',
	backgroundColor: token('elevation.surface'),
	borderRadius: token('radius.xlarge'),
	paddingTop: token('space.200'),
	paddingRight: token('space.200'),
	paddingBottom: token('space.200'),
	paddingLeft: token('space.200'),
	boxShadow: token('elevation.shadow.overlay'),
	width: '280px',
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.200'),
});

const cardStackStyles = css({
	'&::before': {
		position: 'absolute',
		content: '""',
		backgroundColor: token('elevation.surface'),
		boxShadow: token('elevation.shadow.overlay'),
		borderRadius: token('radius.xlarge'),
		bottom: 0,
		left: 0,
		width: '100%',
		height: '100%',
		transformOrigin: '0% 100%',
		zIndex: -1,
		animationName: stackTiltIn,
		animationDuration: '200ms',
		animationTimingFunction: 'ease-out',
		animationFillMode: 'forwards',
	},
	'@media (prefers-reduced-motion: reduce)': {
		'&::before': {
			animation: 'none',
			transform: 'rotate(3deg)',
		},
	},
});

export function SuggestionCard({
	onAccept,
	onDiscard,
	onNavigate,
	onClose,
	suggestion,
	isStacked,
}: {
	isStacked?: boolean;
	onAccept?: () => void;
	onClose?: () => void;
	onDiscard?: () => void;
	onNavigate?: (direction: 'up' | 'down') => void;
	suggestion: string;
}): React.JSX.Element {
	const { formatMessage } = useIntl();

	return (
		<div css={[cardStyles, isStacked && cardStackStyles]}>
			<Flex alignItems="center" justifyContent="space-between">
				<Inline space="space.0" alignBlock="center">
					<IconButton
						/* eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed) */
						icon={() => (
							<ChevronUpIcon
								label={formatMessage(aiSuggestionsMessages.cardExpandButtonLabel)}
								size="small"
							/>
						)}
						label={formatMessage(aiSuggestionsMessages.cardExpandButtonLabel)}
						appearance="subtle"
						spacing="compact"
						isDisabled={!onNavigate}
						onClick={onNavigate ? () => onNavigate('up') : undefined}
					/>
					<IconButton
						/* eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed) */
						icon={() => (
							<ChevronDownIcon
								label={formatMessage(aiSuggestionsMessages.cardCollapseButtonLabel)}
								size="small"
							/>
						)}
						label={formatMessage(aiSuggestionsMessages.cardCollapseButtonLabel)}
						appearance="subtle"
						spacing="compact"
						isDisabled={!onNavigate}
						onClick={onNavigate ? () => onNavigate('down') : undefined}
					/>
				</Inline>
				<IconButton
					icon={CrossIcon}
					label={formatMessage(aiSuggestionsMessages.cardCloseButtonLabel)}
					appearance="subtle"
					spacing="compact"
					onClick={onClose}
				/>
			</Flex>

			<Inline space="space.100" alignBlock="center">
				<RovoIcon
					label={formatMessage(aiSuggestionsMessages.cardRovoTitle)}
					size="small"
					shouldUseNewLogoDesign
					shouldUseHexLogo
				/>
				<Heading size="xsmall">{formatMessage(aiSuggestionsMessages.cardRovoTitle)}</Heading>
				<IconTile
					icon={LockLockedIcon}
					label={formatMessage(aiSuggestionsMessages.cardPrivateLabel)}
					appearance="gray"
					size="xsmall"
				/>
			</Inline>
			<Text as="p" color="color.text.subtle">
				{suggestion}
			</Text>
			<Flex alignItems="center" justifyContent="start" gap="space.050" wrap="wrap">
				<Button iconBefore={CheckMarkIcon} appearance="default" onClick={onAccept}>
					{formatMessage(aiSuggestionsMessages.cardAcceptButtonLabel)}
				</Button>
				<Button iconBefore={DeleteIcon} appearance="subtle" onClick={onDiscard}>
					{formatMessage(aiSuggestionsMessages.cardDiscardButtonLabel)}
				</Button>
			</Flex>
		</div>
	);
}
